import {inject, Injectable, OnDestroy} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {WhitelabelData, WhitelabelService} from '@on-the-go-web/sdk';
import {getColorPalettesObject, getColorPalettes} from '../functions/theme.functions';
import {filter, first, Subject, switchMap, takeUntil} from 'rxjs';

import {AuthService} from './auth.service';
import {LOGO_PATH_TOKEN} from '@on-the-go-web/library';
import {LocalStorageService} from '@on-the-go-web/library/helpers';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private _whitelabelService = inject(WhitelabelService);
  private _logoPath$ = inject(LOGO_PATH_TOKEN);
  private _storageService = inject(LocalStorageService);
  private _authService = inject(AuthService);
  private _document = inject(DOCUMENT);

  private readonly destroy$ = new Subject<void>();

  private _whitelabel = this._storageService.signal('whitelabel');

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTheme(): void {
    if (window?.APP_CONFIG?.WHITELABEL) {
      this._whitelabelService
        .whitelabelGet(window?.APP_CONFIG?.WHITELABEL)
        .pipe(first())
        .subscribe((whitelabelData) => {
          this._whitelabel.set(whitelabelData);
          if (whitelabelData) {
            this._setTheme(whitelabelData);
          }
        });
      return;
    }

    const savedWhitelabel = this._whitelabel();
    if (savedWhitelabel) {
      this._setTheme(savedWhitelabel);
    }

    this._authService.loggedIn$
      .pipe(
        filter((loggedIn) => loggedIn),
        switchMap(() => {
          return this._whitelabelService.whitelabelGet().pipe(first());
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((whitelabelData) => {
        this._whitelabel.set(whitelabelData);
        if (whitelabelData) {
          this._setTheme(whitelabelData);
        }
      });
  }

  private _setTheme(whitelabel: WhitelabelData) {
    this._logoPath$?.next(whitelabel.logo || 'assets/logo_light.png');

    const colorPalettes = getColorPalettes(
      whitelabel?.primaryColor || '#406838',
      whitelabel?.secondaryColor || '#725c00',
      whitelabel?.tertiaryColor || '#a33d26',
    );
    const palettes = getColorPalettesObject(colorPalettes);

    if (whitelabel?.primaryColor) {
      this._setCssVariables('--primary-color', palettes.primary);
    }
    if (whitelabel?.secondaryColor) {
      this._setCssVariables('--secondary-color', palettes.secondary);
    }
    if (whitelabel?.tertiaryColor) {
      this._setCssVariables('--tertiary-color', palettes.tertiary);
    }
  }

  private _setCssVariables(prefix: string, palette: {[key: string]: string}): void {
    for (const key of Object.keys(palette)) {
      this._document.documentElement.style.setProperty(`${prefix}-${key}`, palette[key]);
    }
  }
}
